import { get, post, put } from "@/services/api.services";
import { ContainerType, EntityLookupResult, SavedObject } from "@/services/commonModels";
import axios from "@/lib/apiAxios";
import { registriesAPI } from "../registries/registriesAPI";
import { Attribute } from "../things/models";

export type MaterialSample = {
    type: string;
    descriptor: string | null;
    experimentCode: string;
    thingInstances: { id: string; name: string }[];
    collectionDate: Date | null;
};

export type SavedMaterialSample = MaterialSample &
    SavedObject<string> & {
        sampleSetId: string;
        code: string;
    };

export enum SampleSetStatus {
    DRAFT = "draft",
    CANCELED = "canceled",
    AWAITING_COLLECTION = "awaiting collection",
    RECEIVED = "received",
}

export enum RequestStatus {
    DRAFT = "draft",
    CANCELED = "canceled",
    AWAITING_PROCESSING = "awaiting processing",
    IN_PROGRESS = "in progress",
    COMPLETED = "completed",
}

export type MaterialSampleSet = {
    experimentCode: string;
    status: SampleSetStatus;
    sampleCount: number;
    sampleSopID: string | null;
    materialTypeId: string;
    sampleDescriptorIds: string[];
    sampleSop: SavedMaterialSampleSOP;
};

export type SavedMaterialSampleSet = MaterialSampleSet &
    SavedObject<string> & {
        code: string;
        requestCodes: string[] | null;
    };

export type SaveMaterialSampleSetParams = {
    sampleSopId?: string;
    needByDate?: Date | null;
    status?: SampleSetStatus;
    samples?: UnsavedMaterialSample[];
    materialTypeId?: string | null;
    sampleDescriptorIds?: string[];
};

export type UnsavedMaterialSample = MaterialSample & {
    thingInstanceIds: string[];
};

export type MaterialSamplesContext = {
    sampleSetId?: string;
    sampleSetCode?: string;
    experimentCode: string;
    collectionSOP: MaterialSampleSOP | null;
    materialType: MaterialType | null;
    samplesPerSource: number | null;
    descriptors: SampleDescriptor[];
    needByDate: Date | null;
    things?: { id: string; name: string }[];
    samples?: SavedMaterialSample[];
};

export type BaseRequest = {
    experimentCode: string;
    sourceSampleSetId: string;
    status: RequestStatus;
    needByDate: Date | null;
    requestedByEmail: string;
    protocolId: string;
    processorId: string;
    requestContainerTypeId: string | null;
    samplesContainerTypeId: string | null;
};

export type BaseSavedRequest = BaseRequest & SavedObject<string> & { code: string };

export type Request = Omit<
    BaseRequest,
    | "sourceSampleSetId"
    | "protocolId"
    | "processorId"
    | "requestContainerTypeId"
    | "samplesContainerTypeId"
> & {
    sourceSampleSet: SavedMaterialSampleSet | null;
    protocol: SavedRequestProtocol | null;
    processor: RequestProcessor | null;
    requestContainerType: ContainerType | null;
    samplesContainerType: ContainerType | null;
};

export type SavedRequest = Omit<
    BaseSavedRequest,
    | "sourceSampleSetId"
    | "protocolId"
    | "processorId"
    | "requestContainerTypeId"
    | "samplesContainerTypeId"
> & {
    sourceSampleSet: SavedMaterialSampleSet | null;
    protocol: SavedRequestProtocol | null;
    processor: RequestProcessor | null;
    requestContainerType: ContainerType | null;
    samplesContainerType: ContainerType | null;
};

export type RequestProcessor = {
    id: string;
    name: string;
    type: "internal" | "external";
};

export type RequestProtocol = {
    name: string;
    requestProcessorId: string | null;
    requestContainerTypeId: string | null;
    samplesContainerTypeId: string | null;
    attributes: Attribute[];
};

export type CreateRequestProtocolParams = RequestProtocol & {
    code: string | null;
};

export type SavedRequestProtocol = RequestProtocol &
    SavedObject<string> & {
        code: string;
        url: string;
    };

export type MaterialSampleSOP = {
    name: string;
    materialTypeId: string;
    sampleDescriptorIds: string[];
    // TODO: later when we have the backend creating a Confluence
    // page for the SOP, descriptionURL should probably move to the
    // SavedMaterialSampleSOP model
    descriptionUrl: string;
};

export type SavedMaterialSampleSOP = MaterialSampleSOP &
    SavedObject<string> & {
        code: string;
    };

export type MaterialType = {
    id: string;
    name: string;
};

export type SampleDescriptor = {
    id: string;
    name: string;
};

/**
 * uses v1 (Benchling) data model
 */
export async function fetchSamplesByExperiment(experiment: string, protocol: string) {
    return get(`/samples/exp/${experiment}/protocol/${protocol}`);
}

/**
 * uses v1 (Benchling) data model
 */
export async function fetchSamplesByExperimentAndDate(experimentCode: string, date: unknown) {
    return get(`/samples/${experimentCode}/${date}`);
}

/**
 * uses v1 (Benchling) data model
 */
export async function clearSampleData(sampleIds: string[]) {
    return axios
        .put(`/samples/clearSampleData/`, { ids: sampleIds })
        .then((response) => response.data);
}

/**
 * uses v1 (Benchling) data model
 */
export async function lookupSamplesByExperimentProtocolAndName(
    experiment: string,
    protocol: string,
    sampleNames: string[]
): Promise<EntityLookupResult> {
    return post(`/samples/exp/${experiment}/protocol/${protocol}/lookupSamples`, { sampleNames });
}

export const samplesAPI = {
    /**
     * uses v1 (Benchling) data model
     */
    fetchSamplesProtocols: async () => {
        return get(`/samples/protocols`);
    },

    // v2 endpoints:
    fetchSampleSetsByExperiment: async (
        experimentCode: string
    ): Promise<SavedMaterialSampleSet[]> => {
        return get(`/samples/${experimentCode}/sets`);
    },
    fetchSampleSets: async (): Promise<SavedMaterialSampleSet[]> => {
        return get(`/samples/sets`);
    },
    createSampleSet: async (
        experimentCode: string,
        params: SaveMaterialSampleSetParams
    ): Promise<SavedMaterialSampleSet> => {
        return post(`/samples/${experimentCode}/sets`, params);
    },
    updateSampleSet: async (
        sampleSetId: string,
        params: SaveMaterialSampleSetParams
    ): Promise<SavedMaterialSampleSet> => {
        return put(`/samples/sets/${sampleSetId}`, params);
    },
    fetchSampleSOPs: async (): Promise<SavedMaterialSampleSOP[]> => {
        return get(`/samples/sops`);
    },
    fetchSampleSOPById: async (sopId: string): Promise<SavedMaterialSampleSOP> => {
        return get(`/samples/sops/${sopId}`);
    },
    createSampleSOP: async (params: MaterialSampleSOP): Promise<SavedMaterialSampleSOP> => {
        return post(`/samples/sops`, params);
    },
    updateSampleSOP: async (
        sopId: string,
        params: MaterialSampleSOP
    ): Promise<SavedMaterialSampleSOP> => {
        return put(`/samples/sops/${sopId}`, params);
    },
    fetchSamplesBySetId: async (sampleSetId: string): Promise<SavedMaterialSample[]> => {
        return get(`/samples/sets/${sampleSetId}/samples`);
    },
    downloadSampleSetTemplate: async (sampleSetId: string) => {
        return get(`/samples/excel/${sampleSetId}`, {
            responseType: "blob",
        });
    },
    uploadSamplesFromFile: async (
        sampleSetId: string,
        file: File
    ): Promise<{
        updatedSampleSet?: SavedMaterialSampleSet;
        updatedSamples?: SavedMaterialSample[];
        invalidSamples?: string[];
    }> => {
        const formData = new FormData();
        formData.set("file", file);
        return post(`/samples/excel/${sampleSetId}`, formData);
    },
    fetchRequestByRequestCode: async (requestCode: string): Promise<SavedRequest> => {
        return get(`/samples/requests/${requestCode}`);
    },
    fetchRequests: async (): Promise<SavedRequest[]> => {
        return get(`/samples/requests`);
    },
    fetchRequestsByExperiment: async (experimentCode: string): Promise<SavedRequest[]> => {
        return get(`/samples/requests/experiment/${experimentCode}`);
    },
    createRequest: async (request: BaseRequest): Promise<SavedRequest> => {
        return post(`/samples/requests`, request);
    },
    updateRequest: async (requestId: string, request: BaseRequest): Promise<SavedRequest> => {
        return put(`/samples/requests/${requestId}`, request);
    },
    fetchRequestProtocolByCode: async (protocolCode: string): Promise<SavedRequestProtocol> => {
        return get(`/samples/requests/protocols/${protocolCode}`);
    },
    fetchRequestProtocols: async (): Promise<SavedRequestProtocol[]> => {
        return get(`/samples/requests/protocols`);
    },
    createRequestProtocol: async (
        params: CreateRequestProtocolParams
    ): Promise<SavedRequestProtocol> => {
        return post(`/samples/requests/protocols`, params);
    },
    updateRequestProtocol: async (
        protocolId: string,
        params: RequestProtocol
    ): Promise<SavedRequestProtocol> => {
        return put(`/samples/requests/protocols/${protocolId}`, params);
    },
    fetchMaterialTypes: async (): Promise<MaterialType[]> => {
        const materialTypes =
            await registriesAPI.fetchRegistryEntriesForSchemaName("Material Type");
        return materialTypes.map((materialType) => {
            return {
                id: materialType.id,
                name: materialType.displayName,
            };
        });
    },
    fetchSampleDescriptors: async (): Promise<SampleDescriptor[]> => {
        const descriptors =
            await registriesAPI.fetchRegistryEntriesForSchemaName("Sample descriptor");
        return descriptors.map((descriptor) => {
            return {
                id: descriptor.id,
                name: descriptor.displayName,
            };
        });
    },
    fetchRequestProcessors: async (): Promise<RequestProcessor[]> => {
        const requestProcessors =
            await registriesAPI.fetchRegistryEntriesForSchemaName("Request Processor");
        return requestProcessors.map((processor) => {
            return {
                id: processor.id,
                name: processor.displayName,
                type: processor.data.type as "internal" | "external",
            };
        });
    },
    fetchRequestContainerTypes: async (): Promise<ContainerType[]> => {
        const containerTypes =
            await registriesAPI.fetchRegistryEntriesForSchemaName("container_types");
        return containerTypes.map((containerType) => {
            return {
                id: containerType.id,
                name: containerType.displayName,
                numberOfWells: (containerType.data["num_wells"] as number) || 0,
            };
        });
    },
};
